import React, { useEffect, useState } from "react";
import FiltersPanel from "components/dashboard/Filters/FiltersPanel";
import { useMapFilters } from "hooks/filters";
import PropTypes from "prop-types";
import { ErrorMessage } from "formik";
import { useSchools } from "hooks/map/useSchools";
import { Loader } from "../../../uikit/components";

const CO = "Recreation";
const COName = "СО";
const NP = "Beginner";
const NPName = "НП";
const CC = "Training";
const CCName = "СС";
const CCM = "Improving";
const CCMName = "ССМ";
const BCM = "Higher";
const BCMName = "ВСМ";

const defaultSportTrainingLevelOptions = [
    {
        label: "Все",
        value: {
            name: "Все",
            id: null,
        },
    },
    {
        label: "СО",
        value: {
            id: CO,
            name: COName,
        },
    },
    {
        label: "НП",
        value: {
            id: NP,
            name: NPName,
        },
    },
    {
        label: "СС",
        value: {
            id: CC,
            name: CCName,
        },
    },
    {
        label: "ССМ",
        value: {
            id: CCM,
            name: CCMName,
        },
    },
    {
        label: "ВСМ",
        value: {
            id: BCM,
            name: BCMName,
        },
    },
];

const AllOption = {
    label: "Все",
    value: {
        name: "Все",
        id: null,
    },
};

const AllOrganizationsOption = {
    label: "Все организации",
    value: {
        name: "Все организации",
        id: null,
    },
};

const AllRegionsOption = {
    label: "Вся РФ",
    value: {
        name: "Вся РФ",
        id: null,
    },
};

export default function MapFilter({
    onFilterChange,
    isMapView,
    exportedRegionId,
    sportTypeSelectRef,
    sexSelectRef,
    sportLevelSelectRef,
}) {
    const [sportTypeState, setSportTypeState] = useState(null);
    const [sexState, setSexState] = useState(null);
    const [sportTrainingLevelState, setSportTrainingLevelState] = useState(null);
    const [regionState, setRegionState] = useState(null);
    const [schoolState, setSchoolState] = useState(null);
    const [enableSchoolSelector, setEnableSchoolSelector] = useState(false);
    const { data: schoolsData } = useSchools(regionState?.id, sportTypeState?.id);
    const {
        isLoading: isMapFiltersLoading,
        isError: isMapLoadingError,
        data: mapFiltersData,
    } = useMapFilters();

    useEffect(() => {
        const filter = JSON.parse(localStorage.getItem("mapFilter"));
        if (!filter) return;

        setSportTypeState(filter.sportType ?? null);
        setSexState(filter.sex ?? null);
        setSportTrainingLevelState(filter.sportTrainingLevel ?? null);
        setRegionState(filter.region ?? null);
        setSchoolState(filter.school ?? null);
        onFilterChange(
            filter.sportType?.id ?? null,
            filter.sex?.id ?? null,
            filter.sportTrainingLevel?.id ?? null,
            filter.region?.id ?? null,
            filter.school?.id ?? null,
        );
    }, [onFilterChange]);

    useEffect(() => {
        const filter = {
            sportType: sportTypeState,
            sex: sexState,
            sportTrainingLevel: sportTrainingLevelState,
            region: regionState,
            school: schoolState,
        };
        window.localStorage.setItem("mapFilter", JSON.stringify(filter));
    }, [sportTypeState, sexState, sportTrainingLevelState, regionState, schoolState]);

    function onSelectedSportType(selectedSportType) {
        setSportTypeState(selectedSportType);
        onFilterChange(
            selectedSportType?.id,
            sexState?.id,
            sportTrainingLevelState?.id,
            regionState?.id,
            schoolState?.id,
        );
    }

    function onSelectedSex(selectedSex) {
        setSexState(selectedSex);
        onFilterChange(
            sportTypeState?.id,
            selectedSex?.id,
            sportTrainingLevelState?.id,
            regionState?.id,
            schoolState?.id,
        );
    }

    function onSelectedSportTrainingType(selectedSportTrainingType) {
        setSportTrainingLevelState(selectedSportTrainingType);
        onFilterChange(
            sportTypeState?.id,
            sexState?.id,
            selectedSportTrainingType?.id,
            regionState?.id,
            schoolState?.id,
        );
    }

    function onSelectedRegion(selectedRegion) {
        if (!selectedRegion?.id) {
            setSchoolState(null);
            setEnableSchoolSelector(false);
        } else {
            setSchoolState(null);
            setEnableSchoolSelector(true);
        }
        setRegionState(selectedRegion);
        onFilterChange(
            sportTypeState?.id,
            sexState?.id,
            sportTrainingLevelState?.id,
            selectedRegion?.id,
            null,
        );
    }

    function onSelectedSchool(school) {
        setSchoolState(school);
        onFilterChange(
            sportTypeState?.id,
            sexState?.id,
            sportTrainingLevelState?.id,
            regionState?.id,
            school?.id,
        );
    }

    if (isMapFiltersLoading) {
        return <Loader size={"full"} />;
    }

    if (isMapLoadingError) {
        return (
            <ErrorMessage name=" ">Произошла ошибка при загрузке фильтра для карты.</ErrorMessage>
        );
    }

    let sportTypeOptions = mapFiltersData.sportTypeInfos.map((sportType) => ({
        label: sportType.name,
        value: {
            id: sportType.id,
            name: sportType.name,
        },
    }));
    sportTypeOptions.sort((a, b) => a.label.localeCompare(b.label));

    const hockeyIndex = sportTypeOptions.findIndex((sportType) => sportType.label === "Хоккей");
    if (hockeyIndex !== -1) {
        const hockey = sportTypeOptions[hockeyIndex];
        sportTypeOptions.splice(hockeyIndex, 1);
        sportTypeOptions.unshift(hockey);
    }
    const volleyBallIndex = sportTypeOptions.findIndex(
        (sportType) => sportType.label === "Волейбол",
    );
    if (volleyBallIndex !== -1) {
        const volleyBall = sportTypeOptions[volleyBallIndex];
        sportTypeOptions.splice(volleyBallIndex, 1);
        sportTypeOptions.unshift(volleyBall);
    }

    const samboIndex = sportTypeOptions.findIndex((sportType) => sportType.label === "Самбо");
    if (samboIndex !== -1) {
        const samboo = sportTypeOptions[samboIndex];
        sportTypeOptions.splice(samboIndex, 1);
        sportTypeOptions.unshift(samboo);
    }

    sportTypeOptions.unshift(AllOption);

    const COIndex = mapFiltersData.sportTrainingLevelInfos.findIndex(
        (sportTrainingLevel) => sportTrainingLevel.id === CO && sportTrainingLevel.name === COName,
    );
    const NPIndex = mapFiltersData.sportTrainingLevelInfos.findIndex(
        (sportTrainingLevel) => sportTrainingLevel.id === NP && sportTrainingLevel.name === NPName,
    );
    const CCIndex = mapFiltersData.sportTrainingLevelInfos.findIndex(
        (sportTrainingLevel) => sportTrainingLevel.id === CC && sportTrainingLevel.name === CCName,
    );
    const CCMIndex = mapFiltersData.sportTrainingLevelInfos.findIndex(
        (sportTrainingLevel) =>
            sportTrainingLevel.id === CCM && sportTrainingLevel.name === CCMName,
    );
    const BCMIndex = mapFiltersData.sportTrainingLevelInfos.findIndex(
        (sportTrainingLevel) =>
            sportTrainingLevel.id === BCM && sportTrainingLevel.name === BCMName,
    );

    const receivedIndexesIsGood =
        COIndex !== -1 && NPIndex !== -1 && CCIndex !== -1 && CCMIndex !== -1 && BCMIndex !== -1;

    const sportTrainingLevelOptions = receivedIndexesIsGood
        ? defaultSportTrainingLevelOptions
        : mapFiltersData.sportTrainingLevelInfos.map((sportTrainingLevelInfo) => ({
              label: sportTrainingLevelInfo.name,
              value: {
                  id: sportTrainingLevelInfo.id,
                  name: sportTrainingLevelInfo.name,
              },
          }));

    if (!receivedIndexesIsGood) sportTrainingLevelOptions.unshift(AllOption);

    const sexOptions = mapFiltersData.sexInfos.map((sexInfo) => ({
        label: sexInfo.name,
        value: {
            id: sexInfo.id,
            name: sexInfo.name,
        },
    }));
    sexOptions.unshift(AllOption);

    const regionOptions = mapFiltersData.regionInfos.map((regionInfo) => ({
        label: regionInfo.name,
        value: {
            id: regionInfo.id,
            name: regionInfo.name,
        },
    }));
    regionOptions.sort((a, b) => a.label.localeCompare(b.label));
    regionOptions.unshift(AllRegionsOption);

    const schoolOptions =
        schoolsData?.schoolInfos?.map((schoolInfo) => ({
            label: schoolInfo.name,
            value: {
                id: schoolInfo.id,
                name: schoolInfo.name,
            },
        })) ?? [];
    schoolOptions.sort((a, b) => a.label.localeCompare(b.label));
    schoolOptions.unshift(AllOrganizationsOption);

    let filters = [
        {
            header: "Вид спорта",
            onValueChanged: (data) => onSelectedSportType(data),
            options: sportTypeOptions,
            value: sportTypeState
                ? { label: sportTypeState.name, value: sportTypeState }
                : AllOption,
            reference: sportTypeSelectRef,
        },
        {
            header: "Пол",
            onValueChanged: (data) => onSelectedSex(data),
            options: sexOptions,
            value: sexState ? { label: sexState.name, value: sexState } : AllOption,
            reference: sexSelectRef,
        },
        {
            header: "Этап подготовки",
            onValueChanged: (data) => onSelectedSportTrainingType(data),
            options: sportTrainingLevelOptions,
            value: sportTrainingLevelState
                ? { label: sportTrainingLevelState.name, value: sportTrainingLevelState }
                : AllOption,
            reference: sportLevelSelectRef,
        },
    ];

    if (!isMapView) {
        let isRegionUpdatedNow = false;
        if (!regionState && exportedRegionId) {
            const id = Number(exportedRegionId);
            const exportedRegion = mapFiltersData.regionInfos.find((x) => {
                return x.id === id;
            });
            if (!exportedRegion)
                return (
                    <>Ошибка при поиске региона. Регион с id ' + {exportedRegionId} не найден.</>
                );

            onSelectedRegion({ id: exportedRegionId, name: exportedRegion.name });
            isRegionUpdatedNow = true;
        }

        const currentRegionValue = isRegionUpdatedNow
            ? regionOptions.find((r) => r.value.id === exportedRegionId)
            : regionState;

        filters[3] = {
            header: "Субъект",
            onValueChanged: (data) => onSelectedRegion(data),
            placeHolder: "Выберите регион",
            options: regionOptions,
            value: currentRegionValue
                ? { label: currentRegionValue.name, value: currentRegionValue }
                : AllRegionsOption,
        };

        filters[4] = {
            header: "Спорт. организация",
            onValueChanged: (data) => onSelectedSchool(data),
            options: schoolOptions,
            value: schoolState
                ? { label: schoolState.name, value: schoolState }
                : AllOrganizationsOption,
            isDisabled: !enableSchoolSelector,
        };
    } else {
        if (regionState) {
            onSelectedRegion(null);
        }
    }

    return <FiltersPanel filters={filters} />;
}

MapFilter.propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    isMapView: PropTypes.bool.isRequired,
    exportedRegionId: PropTypes.number,
    sportTypeSelectRef: PropTypes.object,
    sexSelectRef: PropTypes.object,
    sportLevelSelectRef: PropTypes.object,
};
